import React, { useCallback, useEffect, useState } from 'react';

import { Breadcrumb } from '@buildoutinc/blueprint-react/ui/Breadcrumb';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { deleteRequest, getRaw, post, put } from 'helpers/api';
import { formatDate } from 'helpers/formatters/date_time_formats';
import { pluralize } from 'helpers/formatters/string_formats';

import DefaultBehavior from 'bundles/company/document_defaults/shared/default_behavior';
import SavePageModal, { type SaveScope } from 'bundles/company/document_defaults/shared/save_page_modal';

import OrientationBadge from '../orientation_badge';
import ReplaceFileModal from '../replace_file_modal';
import { TemplateType, type Orientation } from '../types';
import DocumentPicker from './document_picker';
import type { PageDetailPayload, PageDetailUsage } from './types';

interface Props {
  basePath: string;
  companyId: string | null;
  libraryUrl: string;
  pageId: string;
  pageType: string;
}

interface FormState {
  pageName: string;
  userLabel: string;
  styleId: string | null;
  options: Record<string, unknown>;
}

type PickerMode = 'add' | 'remove' | null;

const deleteDescription = (count: number) => {
  if (count === 0) return 'This page is not used in any documents.';

  return `Deleting removes this page from ${pluralize(count, 'document')}. This can't be undone.`;
};

const labelDescription = (isTree: boolean) => {
  if (!isTree) return 'Internal only. Visible in the Page Library.';

  return "Internal only. Visible here and when editing a document's page library.";
};

const hasDefaultBehavior = (payload: PageDetailPayload) => {
  const options = payload.all_options || [];
  const styles = payload.page_template_styles || [];

  return options.length > 0 || styles.length > 0;
};

const documentsPhrase = (count: number) => pluralize(count, 'document');

const perDocumentNote = (count: number) => {
  const subject = count === 1 ? '1 document has its own' : `${count} documents have their own`;

  return `${subject} settings for this page. Saving to All Documents replaces them.`;
};

const perCompanyNote = (count: number) => {
  const subject = count === 1 ? '1 company uses its own' : `${count} companies use their own`;

  return `${subject} settings for this page, so they keep those instead of what you save here.`;
};

// A rename can touch no documents at all; "Saved to 0 documents" reads as failure.
const savedMessage = (count: number) => {
  if (count === 0) return 'Your changes are saved.';

  return `Saved to ${documentsPhrase(count)}.`;
};

const replacedMessage = (count: number) => {
  if (count === 0) return 'Replaced the file.';

  return `Replaced the file in ${documentsPhrase(count)}.`;
};

const positionLabel = (usage: PageDetailUsage) => {
  if (usage.last_page) return 'Last page';
  if (usage.position) return `Page ${usage.position}`;

  return 'In library';
};

// A PDF's user_label is the uploader badge, never a label anyone typed, so its
// field seeds from the one stored in metadata instead.
const labelFrom = (payload: PageDetailPayload) => {
  if (payload.page.template_type !== TemplateType.Tree) return payload.page.custom_label ?? '';

  return payload.page_option?.page_template_user_label ?? payload.page.user_label ?? '';
};

const formStateFrom = (payload: PageDetailPayload): FormState => ({
  pageName: payload.page_option?.page_name ?? payload.page.name ?? '',
  userLabel: labelFrom(payload),
  styleId: payload.page_option?.page_template_style_id ?? null,
  options: (payload.page_option?.options as Record<string, unknown> | null) ?? {},
});

export default function PageDetail({ basePath, companyId, libraryUrl, pageId, pageType }: Props) {
  const [payload, setPayload] = useState<PageDetailPayload | null>(null);
  const [initialForm, setInitialForm] = useState<FormState | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pickerMode, setPickerMode] = useState<PickerMode>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [actionFailed, setActionFailed] = useState(false);
  const [savedCount, setSavedCount] = useState<number | null>(null);
  const [replacing, setReplacing] = useState(false);
  const [replacedCount, setReplacedCount] = useState<number | null>(null);

  const companyQuery = companyId ? `?company_id=${companyId}` : '';
  const pageUrl = `${basePath}/${pageType}/${pageId}`;
  const membershipsUrl = `${basePath.replace(/\/pages$/, '')}/documents`;

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response: PageDetailPayload = await getRaw(`${pageUrl}/details${companyQuery}`);
      setError(false);
      setPayload(response);
      const state = formStateFrom(response);
      setInitialForm(state);
      setForm(state);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [pageUrl, companyQuery]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const updateForm = (changes: Partial<FormState>) => {
    setSavedCount(null);
    setReplacedCount(null);
    setForm(current => (current ? { ...current, ...changes } : current));
  };

  const save = async (scope: SaveScope = { allDocuments: true, documentIds: [] }) => {
    if (!form) return;

    setSaving(true);
    setActionFailed(false);
    setSavedCount(null);
    try {
      const response: { documentsCount: number } = await put(`${pageUrl}${companyQuery}`, {
        pageDefaults: {
          pageName: form.pageName,
          pageTemplateUserLabel: form.userLabel,
          pageTemplateStyleId: form.styleId,
          options: form.options,
        },
        documentTemplateIds: scope.allDocuments ? [] : scope.documentIds,
      });
      setShowSaveModal(false);
      setSavedCount(response.documentsCount);
      await fetchDetails();
    } catch {
      // Close the modal so the failure alert isn't hidden under its overlay.
      setShowSaveModal(false);
      setActionFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const membershipPayload = (documentIds: number[]) => ({
    pageType: payload?.page.template_type,
    pageId: payload?.page.id,
    documentTemplateIds: documentIds,
    companyId: companyId || undefined,
  });

  const submitPicker = async (documentIds: number[]) => {
    if (!payload) return;

    setSaving(true);
    setActionFailed(false);
    setSavedCount(null);
    try {
      if (pickerMode === 'add') {
        await post(membershipsUrl, membershipPayload(documentIds));
      } else {
        // deleteRequest's second argument is axios config, not a body.
        await deleteRequest(membershipsUrl, { params: membershipPayload(documentIds) });
      }
      setPickerMode(null);
      await fetchDetails();
    } catch {
      setActionFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const removeFromDocument = async (usage: PageDetailUsage) => {
    if (!payload || saving) return;

    setSaving(true);
    setActionFailed(false);
    setSavedCount(null);
    try {
      await deleteRequest(membershipsUrl, { params: membershipPayload([usage.document_template_id]) });
      await fetchDetails();
    } catch {
      setActionFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const handleReplaced = async (count: number) => {
    setReplacing(false);
    setActionFailed(false);
    setSavedCount(null);
    setReplacedCount(count);
    await fetchDetails();
  };

  const destroyPage = async () => {
    setSaving(true);
    setActionFailed(false);
    try {
      await deleteRequest(`${pageUrl}${companyQuery}`);
      window.location.href = libraryUrl;
    } catch {
      setActionFailed(true);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container my-5 text-center">
        <div className="spinner-border spinner-xl" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !payload || !form || !initialForm) {
    return (
      <div className="container my-5">
        <Alert severity="destructive">
          <Alert.Title>This page could not be loaded.</Alert.Title>
          <Button variant="secondary" onClick={fetchDetails}>Try again</Button>
        </Alert>
      </div>
    );
  }

  const { page, usages, file, documents } = payload;
  const isTree = page.template_type === TemplateType.Tree;
  // Saved pages have nowhere to store a label; PDFs keep theirs in metadata.
  const showsLabel = isTree || page.template_type === TemplateType.Pdf;
  const dirty = JSON.stringify(form) !== JSON.stringify(initialForm);
  const nameMissing = !isTree && form.pageName.trim() === '';
  const perDocumentCount = payload.per_document_option_count ?? 0;
  const childCompanyCount = payload.child_company_option_count ?? 0;
  // usages spans the family (PageUsages walks the parent and its companies), but
  // the endpoint only accepts this context's own templates, so the chooser is
  // built from the candidates rather than from the usage rows.
  const saveableDocuments = documents.filter(doc => doc.in_document).map(doc => ({ id: doc.id, name: doc.name }));

  const handleSaveClick = () => {
    if (isTree && usages.length > 0) {
      setShowSaveModal(true);
    } else {
      save();
    }
  };

  return (
    <div className="container my-4 pb-7">
      <Breadcrumb className="mb-3">
        <Breadcrumb.List>
          <Breadcrumb.Item>
            <Breadcrumb.Link href={libraryUrl}>Page Library</Breadcrumb.Link>
          </Breadcrumb.Item>
          <Breadcrumb.Separator />
          <Breadcrumb.Item>
            <Breadcrumb.Page>{page.name}</Breadcrumb.Page>
          </Breadcrumb.Item>
        </Breadcrumb.List>
      </Breadcrumb>

      <h4 className="mb-1">{page.name}</h4>
      <p className="text-muted mb-4">
        Used in {documentsPhrase(page.documents_count)}
        {page.updated_at && ` · Last edited ${formatDate(page.updated_at)}`}
      </p>

      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-1">Page settings</h5>
          <p className="text-muted mb-3">The name advisors see, and an internal label for your team.</p>
          <div className="row">
            <div className="col-md-6">
              <Field>
                <Field.Label>Page name</Field.Label>
                <Input
                  disabled={!page.editable}
                  placeholder={page.name}
                  value={form.pageName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateForm({ pageName: e.target.value })}
                />
                {!page.editable && (
                  <Field.Description>
                    Only {page.created_by_name} can edit this page. You can still add or remove it from documents.
                  </Field.Description>
                )}
                {perDocumentCount > 0 && (
                  <Field.Description>{perDocumentNote(perDocumentCount)}</Field.Description>
                )}
                {childCompanyCount > 0 && (
                  <Field.Description>{perCompanyNote(childCompanyCount)}</Field.Description>
                )}
              </Field>
            </div>
            {showsLabel && (
              <div className="col-md-6">
                <Field>
                  <Field.Label>Page template label</Field.Label>
                  <Input
                    disabled={!page.editable}
                    value={form.userLabel}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateForm({ userLabel: e.target.value })}
                  />
                  <Field.Description>{labelDescription(isTree)}</Field.Description>
                </Field>
              </div>
            )}
          </div>
          <div className="d-flex align-items-center gap-2 mt-3">
            <span className="fw-semibold">Orientation:</span>
            <OrientationBadge orientation={page.orientation as Orientation} />
            {page.size_label && <span className="text-muted">{page.size_label}</span>}
            <span className="text-muted">
              Set by the uploaded file. A page can only go in documents with a matching orientation.
            </span>
          </div>
          {file && (
            <div className="d-flex align-items-center gap-2 mt-2">
              <span className="fw-semibold">File:</span>
              <span>{file.file_name || 'Unknown file'}</span>
              <span className="text-muted">
                {file.replaced_at && `Replaced ${formatDate(file.replaced_at)} · `}
                live in {documentsPhrase(page.documents_count)}
              </span>
              <Button
                disabled={!page.editable || saving}
                size="sm"
                variant="secondary"
                onClick={() => setReplacing(true)}
              >
                Replace file
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <h5 className="mb-0">Where this page is used</h5>
            <div className="d-flex gap-2">
              <Button
                disabled={page.documents_count === 0}
                size="sm"
                variant="secondary"
                onClick={() => setPickerMode('remove')}
              >
                Remove from documents
              </Button>
              <Button size="sm" variant="primary" onClick={() => setPickerMode('add')}>
                Add to documents
              </Button>
            </div>
          </div>
          <p className="text-muted mb-3">Add or remove documents here instead of opening each one.</p>
          {usages.length === 0 ? (
            <Empty>
              <Empty.Title>This page is not used in any documents</Empty.Title>
              <Empty.Content>Add it to documents to include it when they are generated.</Empty.Content>
            </Empty>
          ) : (
            <Table className="align-middle">
              <Table.Header>
                <Table.Row>
                  <Table.Head>Document</Table.Head>
                  <Table.Head>Type</Table.Head>
                  <Table.Head>Position</Table.Head>
                  <Table.Head />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {usages.map(usage => (
                  <Table.Row key={usage.document_template_id}>
                    <Table.Cell>{usage.document_template_name}</Table.Cell>
                    <Table.Cell className="text-muted">{usage.document_type || '---'}</Table.Cell>
                    <Table.Cell className="text-muted">{positionLabel(usage)}</Table.Cell>
                    <Table.Cell className="text-end">
                      <Button disabled={saving} size="sm" variant="ghost" onClick={() => removeFromDocument(usage)}>
                        Remove
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Card.Body>
      </Card>

      {isTree && !hasDefaultBehavior(payload) && (
        <p className="text-muted mt-5">This page has no layout or option settings.</p>
      )}

      {isTree && hasDefaultBehavior(payload) && (
        <DefaultBehavior
          allOptions={payload.all_options || []}
          options={form.options}
          styleId={form.styleId}
          styles={payload.page_template_styles || []}
          onOptionsRemove={(names) => {
            const next = { ...form.options };
            names.forEach((name) => {
              delete next[name];
            });
            updateForm({ options: next });
          }}
          onOptionsUpdate={changes => updateForm({ options: { ...form.options, ...changes } })}
          onStyleRemove={() => updateForm({ styleId: null })}
          onStyleUpdate={layout => updateForm({ styleId: layout.identifier })}
        />
      )}

      {page.deletable && (
        <Card className="mb-4">
          <Card.Body>
            <h5 className="mb-2">Delete this page</h5>
            <p className="text-muted">{deleteDescription(page.documents_count)}</p>
            <Button variant="destructive" onClick={() => setConfirmingDelete(true)}>Delete page</Button>
          </Card.Body>
        </Card>
      )}

      {actionFailed && (
        <Alert className="mb-3" severity="destructive">
          <Alert.Title>There was an error saving your page, please try again.</Alert.Title>
        </Alert>
      )}

      {savedCount !== null && (
        <Alert className="mb-3" severity="success">
          <Alert.Title>{savedMessage(savedCount)}</Alert.Title>
        </Alert>
      )}

      {replacedCount !== null && (
        <Alert className="mb-3" severity="success">
          <Alert.Title>{replacedMessage(replacedCount)}</Alert.Title>
        </Alert>
      )}

      <div className="position-sticky bottom-0 bg-white border-top py-3 d-flex justify-content-end gap-2">
        <Button href={libraryUrl} variant="ghost">Cancel</Button>
        <Button
          disabled={!dirty || saving || nameMissing || !page.editable}
          variant="primary"
          onClick={handleSaveClick}
        >
          {saving ? 'Saving ...' : 'Save changes'}
        </Button>
      </div>

      <DocumentPicker
        documents={documents}
        key={pickerMode ?? 'closed'}
        mode={pickerMode || 'add'}
        pageOrientation={page.orientation}
        saving={saving}
        show={pickerMode !== null}
        onClose={() => setPickerMode(null)}
        onSubmit={submitPicker}
      />

      {file && (
        <ReplaceFileModal
          basePath={basePath}
          companyId={companyId}
          documentCount={page.documents_count}
          documents={usages.map(usage => ({ id: usage.document_template_id, name: usage.document_template_name }))}
          key={replacing ? 'open' : 'closed'}
          page={{ id: page.id, name: page.name, orientation: page.orientation, fileName: file.file_name }}
          show={replacing}
          onClose={() => setReplacing(false)}
          onReplaced={handleReplaced}
        />
      )}

      <SavePageModal
        documents={saveableDocuments}
        key={showSaveModal ? 'open' : 'closed'}
        saving={saving}
        show={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={save}
      />

      <Modal open={confirmingDelete} onClose={() => !saving && setConfirmingDelete(false)}>
        <Modal.Content centered>
          <Modal.Header>
            <Modal.Title>Delete this page?</Modal.Title>
          </Modal.Header>
          <Modal.Body>{deleteDescription(page.documents_count)}</Modal.Body>
          <Modal.Footer>
            <Button disabled={saving} variant="ghost" onClick={() => setConfirmingDelete(false)}>Cancel</Button>
            <Button disabled={saving} variant="destructive" onClick={destroyPage}>Delete page</Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </div>
  );
}
